import { reactive, readonly } from 'vue';
import { BiometricService } from '@/services/biometricService';
import { BIOMETRIC_AUTH } from '@/utils/constants';
import i18n from '@/i18n';

interface BiometricState {
    isOpen: boolean;
    isLoading: boolean;
    title: string;
    message: string;
    error: string | null;
}

const state = reactive<BiometricState>({
    isOpen: false,
    isLoading: false,
    title: '',
    message: '',
    error: null,
});

let resolveAuth: (value: boolean) => void;

export function useBiometric() {

    // Triggers the native prompt immediately with no custom UI beforehand.
    const silentAuthenticate = async (
        action: () => Promise<any> | any,
        options?: { onSuccess?: (res: any) => void; onError?: (err: any) => void }
    ) => {
        const availability = await BiometricService.checkAvailability();

        if (!availability.isAvailable) {
            const result = await action();
            options?.onSuccess?.(result);
            return;
        }

        try {
            await BiometricService.verifyIdentity(
                i18n.global.t(BIOMETRIC_AUTH.KEYS.DEFAULT_MESSAGE),
                i18n.global.t(BIOMETRIC_AUTH.KEYS.DEFAULT_TITLE),
                i18n.global.t(BIOMETRIC_AUTH.KEYS.DEFAULT_DESCRIPTION),
                i18n.global.t(BIOMETRIC_AUTH.KEYS.NEGATIVE_BUTTON)
            );

            const result = await action();
            options?.onSuccess?.(result);
        } catch (err: any) {
            options?.onError?.(err);
        }
    };

    // Shows a custom modal explaining the action, then triggers the native prompt.
    const authenticateAndRun = async (
        action: () => Promise<any> | any,
        options?: {
            title?: string;
            message?: string;
            onSuccess?: (res: any) => void;
            onError?: (err: any) => void;
        }
    ) => {
        const availability = await BiometricService.checkAvailability();

        if (!availability.isAvailable) {
            const result = await action();
            options?.onSuccess?.(result);
            return;
        }

        state.title = options?.title ?? BIOMETRIC_AUTH.KEYS.DEFAULT_TITLE;
        state.message = options?.message ?? BIOMETRIC_AUTH.KEYS.DEFAULT_MESSAGE;
        state.error = null;
        state.isOpen = true;

        const userConfirmed = await new Promise<boolean>((res) => { resolveAuth = res; });

        if (!userConfirmed) {
            state.isOpen = false;
            return;
        }

        state.isLoading = true;
        state.error = null;

        try {
            await BiometricService.verifyIdentity(
                i18n.global.t(state.message),
                i18n.global.t(state.title),
                i18n.global.t(BIOMETRIC_AUTH.KEYS.DEFAULT_DESCRIPTION),
                i18n.global.t(BIOMETRIC_AUTH.KEYS.NEGATIVE_BUTTON)
            );

            state.isOpen = false;
            state.isLoading = false;

            const result = await action();
            options?.onSuccess?.(result);

        } catch (err: any) {
            state.isLoading = false;

            if (err.code === BIOMETRIC_AUTH.CODES.USER_CANCELLED) {
                state.error = BIOMETRIC_AUTH.KEYS.USER_CANCELLED;
            } else if (err.code === BIOMETRIC_AUTH.CODES.LOCKOUT) {
                state.error = BIOMETRIC_AUTH.KEYS.LOCKOUT;
            } else if (err.code === BIOMETRIC_AUTH.CODES.AUTH_FAILED) {
                state.error = BIOMETRIC_AUTH.KEYS.AUTH_FAILED;
            } else {
                state.error = BIOMETRIC_AUTH.KEYS.GENERIC_ERROR;
            }

            options?.onError?.(err);
        }
    };

    const handleConfirm = () => resolveAuth?.(true);
    const handleCancel = () => {
        state.isOpen = false;
        resolveAuth?.(false);
    };

    return {
        state: readonly(state),
        silentAuthenticate,
        authenticateAndRun,
        handleConfirm,
        handleCancel,
    };
}